export * from './booking-confirmation';
export type ENVS = 'dev' | 'stg' | 'prd';

import { TenantId } from '@pelicargo/tenants';
import type {
  Account,
  AccountInvite,
  Aircraft,
  Cargo,
  Contact,
  Document,
  FlightSegment,
  Instruction,
  LineItem,
  Provider,
  ProviderAccount,
  ProviderDataMap,
  Quote,
  QuoteVariant,
  Rate,
  RateImport,
  Request,
  Surcharge,
  TieredCost,
  User,
} from '@prisma/client';
import {
  AccountType,
  AircraftRestriction,
  AircraftType,
  BacklogStatus,
  BatteryType,
  BusinessType,
  CargoCooling,
  CargoCoolingRange,
  CargoTypeSub,
  CostUnitType,
  DimensionUnit,
  DocumentType,
  EarliestTenderOption,
  ExpiryReason,
  FreightService,
  FreightServiceAddOn,
  FreightStatus,
  HandlingOptions,
  LineItemType,
  MarkupCategory,
  NotificationCategory,
  OnboardingStep,
  PackingGroup,
  PackingInstruction,
  ProviderDataMapType,
  QuoteStatus,
  RateCategory,
  RateImportState,
  RequestStatus,
  Section,
  ServiceType,
  SourceType,
  SurchargeCategory,
  SurchargeUnit,
  SurchargeWeightType,
  TimeOfDay,
  TrackingShipmentStatus,
  VariantType,
  WeightUnit,
} from '@prisma/client';

// Prisma doesn't export enums that are define in schema but not used in schema
enum InlandCategory {
  PICK_UP = 'PICK_UP',
  AWB_LABELING = 'AWB_LABELING',
  TRANSFER = 'TRANSFER',
}

export {
  Account,
  AccountInvite,
  AccountType,
  Aircraft,
  AircraftRestriction,
  AircraftType,
  BacklogStatus,
  BatteryType,
  BusinessType,
  Cargo,
  CargoCooling,
  CargoCoolingRange,
  CargoTypeSub,
  Contact,
  CostUnitType,
  DimensionUnit,
  Document,
  DocumentType,
  EarliestTenderOption,
  ExpiryReason,
  FlightSegment,
  FreightService,
  FreightServiceAddOn,
  FreightStatus,
  HandlingOptions,
  InlandCategory,
  Instruction,
  LineItem,
  LineItemType,
  MarkupCategory,
  NotificationCategory,
  OnboardingStep,
  PackingGroup,
  PackingInstruction,
  Provider,
  ProviderAccount,
  ProviderDataMap,
  ProviderDataMapType,
  Quote,
  QuoteStatus,
  QuoteVariant,
  Rate,
  RateCategory,
  RateImport,
  RateImportState,
  Request,
  RequestStatus,
  Section,
  ServiceType,
  SourceType,
  Surcharge,
  SurchargeCategory,
  SurchargeUnit,
  SurchargeWeightType,
  TieredCost,
  TimeOfDay,
  TrackingShipmentStatus,
  User,
  VariantType,
  WeightUnit,
};

export enum TypesenseCollection {
  ACCOUNTS = 'accounts',
  AIRPORTS = 'airports',
  AIRLINES = 'airlines',
  AIRCRAFTS = 'aircrafts',
  REQUESTS = 'requests',
  CONTACTS = 'contacts',
}

export enum CargoType {
  // PrismaCargoType
  GeneralCargo = 'GeneralCargo',
  Perishable = 'Perishable',
  DangerousGoods = 'DangerousGoods',
  Pharma = 'Pharma',
  Valuable = 'Valuable',

  // UI Cargo Type
  Batteries = 'Batteries',
}

export enum DataType {
  'STRING' = 'STRING',
  'NUMBER' = 'NUMBER',
  'DATE' = 'DATE',
  'BOOLEAN' = 'BOOLEAN',
  'JSON' = 'JSON',
  'IGNORE' = 'IGNORE',
}

// https://stackoverflow.com/a/53808212
export type IfEquals<T, U, Y = unknown, N = never> =
  (<G>() => G extends T ? 1 : 2) extends <G>() => G extends U ? 1 : 2 ? Y : N;
export const assertType: <T, U>(draft: T & IfEquals<T, U>, expected: U & IfEquals<T, U>) => IfEquals<T, U> =
  (() => {}) as any;

export type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export type RequireOnly<T, K extends keyof T> = {
  [Key in K]: T[Key];
} & {
  [Key in Exclude<keyof T, K>]?: T[Key];
};

export type ValueOf<T> = T[keyof T];

export type KeysOfUnion<T> = T extends T ? keyof T : never;

export type UnionToIntersection<U> = (U extends any ? (x: U) => void : never) extends (x: infer I) => void ? I : never;

export type KeysMatching<T extends object, V> = {
  [K in keyof T]-?: T[K] extends V ? K : never;
}[keyof T];

type Impossible<K extends keyof any> = {
  [P in K]: never;
};

export type Exact<T, U extends T = T> = U & Impossible<Exclude<keyof U, keyof T>>;
export type OptionalField<T, K extends keyof T> = Exclude<T, K> & { [Key in K]?: T[Key] };

// Copied from flight segment reorder branch
type TypeWithGeneric<T> = T[];
type FuncWithGeneric<T> = (arg: T) => any;
export type ExtractGeneric<Type> = Type extends TypeWithGeneric<infer X> ? X : never;
export type ExtractGenericFromFunction<Type extends Function> = Type extends FuncWithGeneric<infer X> ? X : never;

export type ExtractTypeFromTRPCSubscription<T extends { subscribe: <T>(T) => any }> = Parameters<
  ExtractGenericFromFunction<T['subscribe']>['next']
>[0];

interface HasId {
  id: string | number;
}

export const keysOf = <Arg extends object>(arg: Arg) => Object.keys(arg) as (keyof Arg)[];

export type Shell<T extends HasId> = RequireOnly<T, 'id'>;

// Typesense
export type TSAirport = {
  id: string;
  airport_name: string;
  airport_time_zone: string;
  airport_id: string;
  airport_latlng: [number, number];
  airport_main_gateway: boolean;
  airport_country: string;
  tenant_ids: TenantId[];
};

export type TSAccount = {
  id: string;
  account_id: string;
  name: string;
  highlight_until: number;
  tenant_ids: TenantId[];
};

export type TSAirline = {
  id: string;
  airline_name: string;
  airline_id: string;
  tenant_ids: TenantId[];
};

export type TSAircraft = {
  id: string;
  aircraft_name: string;
  aircraft_type: string;
  tenant_ids: TenantId[];
};

export type TSContact = {
  id: string;
  created_at: number;
  updated_at?: number;
  preferred_name?: string;
  full_name?: string;
  email: string;
  phone?: string;
  associations: {
    tags?: string[];
    accounts?: RequireOnly<Account, 'id' | 'name'>[];
    airlines?: string[];
    airports?: string[];
    tenants?: string[];
    has_airports: boolean;
    has_accounts: boolean;
    has_tags: boolean;
  }[];
  tenant_ids: TenantId[];
};

export type TSRequest = {
  id: string;
  formatted_id: string;
  account_id: string;
  account_type: string;
  request_status: string;
  origins: string[];
  destinations: string[];
  author_email: string;
  created_at: number;
  dropoff_by: number;
  is_known_shipper: boolean;
  is_freighter_required: boolean;
  cargo_type: string;
  quote_status_counts: Record<QuoteStatus, number>;
  color?: string;
  classifications?: { name: string; color: string }[];
  main_request_id: string;
  linked_request_ids: string[];
  awb_numbers: string[];
  weight_break: number;
  payment_status: string;
  shipment_status: string;
  tenant_ids: TenantId[];
};

// Manually Created Types

export enum UnNumber {
  UN3480 = '3480',
  UN3481 = '3481',
  UN3090 = '3090',
  UN3091 = '3091',
  UN3171 = '3171',
}

export enum BatteryTypeVariant {
  LiIon_UN3481_PI967 = 'LiIon_UN3481_PI967',
  LiIon_UN3481_PI966 = 'LiIon_UN3481_PI966',
  LiMetal_UN3091_PI969 = 'LiMetal_UN3091_PI969',
  LiMetal_UN3091_PI970 = 'LiMetal_UN3091_PI970',
}

export type Address = {
  // Business or recipient name
  recipient?: string;
  // Main address
  line_one?: string;
  // Apt/building number
  line_two?: string;
  // Country name (i.e. Canada, Germany, etc.)
  country?: string;
  // Province or State name (i.e. BC, Washington, etc.)
  state?: string;
  // City name (i.e. Victoria, Kelowna, etc.)
  city?: string;
  // Zip or postal code (i.e. V8X0B7, 10295, etc.)
  zip_code?: string;
};

export enum EstimatedTransitTime {
  OneToTwoDays = '1-2 days',
  TwoToThreeDays = '2-3 days',
  ThreeToFourDays = '3-4 days',
  FourToFiveDays = '4-5 days',
  FiveToSixDays = '5-6 days',
  SixToSevenDays = '6-7 days',
  SevenDaysPlus = '7+ days',
}

export enum ClarificationReason {
  IncorrectDetails = 'IncorrectDetails',
  Oversize = 'Oversize',
  DangerousGoods = 'DangerousGoods',
  TemperatureControl = 'TemperatureControl',
  Other = 'Other',
}

export enum CancelReason {
  CargoDelay = 'CargoDelay',
  TransitTime = 'TransitTime',
  Rate = 'Rate',
  Unknown = 'Unknown',
}

export type SelectOption = {
  label: string;
  value: string;
};

export type MaxAircraftParams = {
  length: number;
  width: number;
  height: number;
  weight_per_piece: number;
};

export type Hint = {
  name: string;
  displayed_at: Date | null;
  content: {
    title: string;
    description: string;
  };
};
export enum QuoteStage {
  ZERO = 'ZERO',
  ONE = 'ONE',
  TWO = 'TWO',
  THREE = 'THREE',
  FOUR = 'FOUR',
}

export enum DrawerType {
  ProviderQuoting = 'ProviderQuoting',
  QuoteUpdate = 'QuoteUpdate',
  AirlineBooking = 'AirlineBooking',
  BookingConfirmation = 'BookingConfirmation',
  BookingChange = 'BookingChange',
  BookingCancel = 'BookingCancel',
  NewRate = 'NewRate',
  Contact = 'Contact',
  RequestClarifications = 'RequestClarifications',
  RequestSchedule = 'RequestSchedule',
}

export enum WeightType {
  PerPiece = 'Weight Per Piece',
  Gross = 'Gross Weight',
}

export type FormType = 'Create' | 'Update';

/**
 * Documents — the inputs to the "may this viewer retype or delete it" decision. The rule itself is
 * `modelUtils.document.checkManage` in @pelicargo/utils; these live here so the API, the customer app and the
 * rule all describe it with the same vocabulary.
 */

/**
 * The fields a manage decision reads. A `Pick` rather than the full row so both sides can satisfy it:
 * the API passes a real Prisma row, the clients pass whatever the tRPC payload gave them.
 */
export type ManageableDocument = Pick<
  Document,
  'id' | 'account_id' | 'request_id' | 'author_id' | 'approved_at' | 'provider_id' | 'created_at'
>;

/** Who is asking. `accountId` is the account being *acted as* — client-supplied, so never proof of identity. */
export type DocumentViewer = {
  accountId?: number | null;
  userId?: number | null;
  /**
   * Tenant staff, CSMEMBER and above. Waives ownership and the integrity rules, because undoing a
   * wrong approval or clearing a bad provider import is exactly their job. Callers are expected to
   * have established this from permissions, never from client input.
   */
  isStaff?: boolean;
};

export type DocumentManageDenial =
  /** No document, or one that has no row yet (a placeholder for an expected type). */
  | 'not-persisted'
  /** Attributed to another account. */
  | 'not-owner'
  /** On a request but attributed to nobody — no one can prove it's theirs. */
  | 'unattributable'
  /** A loose upload belonging to a different user. */
  | 'not-author'
  /** An anonymous pre-login upload that is too old to still be claimed by an anonymous caller. */
  | 'stale-anonymous'
  /** The counterparty has approved it; changing it now would invalidate that approval. */
  | 'approved'
  /** Owned by a provider integration, which overwrites it on the next sync. */
  | 'provider-owned';

/**
 * `reason`/`message` are declared (as `undefined`) on the success arm too. The API compiles with
 * `strict: false`, which disables the narrowing that would otherwise let `if (!result.ok)` reveal
 * them; spelling them out keeps the union usable there without giving up the discriminant.
 */
export type DocumentManageResult =
  | { ok: true; reason?: undefined; message?: undefined }
  | { ok: false; reason: DocumentManageDenial; message: string };

/** A document plus the account it was uploaded under, the way the document queries return it. */
export type OwnedDocument = Partial<Document> & { account?: { id?: number; name?: string | null } | null };

/** A document as it appears in a group: the document plus the row label the grouping resolved. */
export type GroupedDocument = OwnedDocument & { label?: string };

/** Documents belonging to one account, as `modelUtils.documents.groupByAccount` (@pelicargo/utils) returns them. */
export type DocumentGroup = {
  key: string;
  title: string;
  documents: GroupedDocument[];
};

export type CargoItem = {
  quantity: number;
  length: number;
  width: number;
  height: number;
  weight: number;
  handling: HandlingOptions;
  packing: HandlingOptions[];
};

export type ExtendedAddress = Partial<{
  recipient: string;
  line_one: string;
  line_two: string;
  country: string;
  state: string;
  city: string;
  zip_code: string;
  name: string;
  latitude: number;
  longitude: number;
  place_id: string;
}>;

export type RequestFormValues = {
  origin_airports: string[];
  destination_airports: string[];
  dropoff_by: string;
  commodity: string;
  original_dimension_unit: DimensionUnit;
  original_weight_unit: WeightUnit;
  is_known_shipper?: boolean;
  is_pre_screened?: boolean;
  cargo_origin?: ExtendedAddress;
  cargo_details: string;
  cargo_type: CargoType;
  cargo_type_sub?: CargoTypeSub;
  cargo_cooling_range?: CargoCoolingRange | '';
  cargo_cooling?: CargoCooling | '';
  aircraft_types: string[];
  cargo: CargoItem[];
  // TODO: Make part of form
  total_pieces: number;
  volumetric_weight: number;
  gross_weight: number;

  flight: string;
  service_type: ServiceType;
  freight_service?: FreightService;
  extra_freight_services?: FreightServiceAddOn[];
  is_consolidated: boolean;
  // Carries the AI run id from the importer through to createRequest so the
  // created Request links back to its AgentExecution and the correction is recorded.
  agent_execution_id?: number;

  // DGD
  un_number: string;
  class: string;
  packing_group: PackingGroup | '';
  aircraft_restriction: AircraftRestriction | '';
  packing_instruction: PackingInstruction | '';
  section: Section | '';
  battery_type: BatteryType | '';
  battery_type_variant: BatteryTypeVariant | '';

  // documents
  documents: Partial<Document>[];

  // Default contacts
  default_contacts: {
    contact_id: number;
    updates_for: NotificationCategory[];
  }[];
};
